type TokenInfo = {
  address: string;
  symbol: string;
  decimals: number;
};

type Escrow = {
  escrow_address: string;
  fee_collector: string;
  duration: string;
  start_time: string;
  deadline: string;
  status: number;

  seller_account: string;
  seller_nickname: string | null;
  seller_exchange_token: TokenInfo;
  seller_exchange_amount: bigint;
  seller_fee_token: TokenInfo;
  seller_fee_amount: bigint;
  seller_penalty_token: TokenInfo;
  seller_penalty_amount: bigint;

  buyer_account: string;
  buyer_nickname: string | null;
  buyer_exchange_token: TokenInfo;
  buyer_exchange_amount: bigint;
  buyer_fee_token: TokenInfo;
  buyer_fee_amount: bigint;
  buyer_penalty_token: TokenInfo;
  buyer_penalty_amount: bigint;

  seller_penalty_deposited: boolean;
  seller_assets_deposited: boolean;
  buyer_penalty_deposited: boolean;
  buyer_assets_deposited: boolean;

  block_number: string;
  block_timestamp: string;
  created_at: string;
};

type Transaction = {
  tx: string;
  timestamp: string;
  transaction_name: string;
  transaction_payload: unknown;
};

// --- Utils ---
const reviveBigInts = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === "object" && !Array.isArray(obj)) {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (
        (key.endsWith("_amount") || key.endsWith("deadline")) &&
        typeof value === "string"
      ) {
        newObj[key] = BigInt(value);
      } else {
        newObj[key] = reviveBigInts(value);
      }
    }
    return newObj;
  }

  if (Array.isArray(obj)) {
    return obj.map((el) => reviveBigInts(el));
  }

  return obj;
};

// --- API ---
const getEscrows = async (): Promise<Escrow[]> => {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/escrow`, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Error fetching escrow");
  }

  const data = await response.json();
  return reviveBigInts(data) as Escrow[];
};

const getEscrowByAddress = async (address: string): Promise<Escrow> => {
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/escrow/${address}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    },
  );
  if (!response.ok) {
    throw new Error("Error fetching escrow");
  }

  const data = await response.json();
  return reviveBigInts(data) as Escrow;
};

const getEscrowTransactions = async (
  address: string,
): Promise<Transaction[]> => {
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/transactions/${address}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    },
  );
  if (!response.ok) {
    throw new Error("Error fetching escrow transactions");
  }

  return await response.json();
};

export type { Escrow, Transaction };
export { getEscrows, getEscrowByAddress, getEscrowTransactions };
